package com.evoapps.evoshift.domain.rosterimport

import com.evoapps.evoshift.domain.model.AbsenceType
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.ShiftTemplate
import com.evoapps.evoshift.domain.usecase.shift.ShiftFactory
import com.evoapps.evoshift.domain.util.overlaps
import com.evoapps.evoshift.domain.util.plannedTimeRange
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.number
import kotlinx.datetime.plus

/*
 * SH-217: importing a month's roster from a spreadsheet (pasted cells, .csv, .xlsx). Nothing is
 * guessed about the layout: the person points at the column with the names and at the cell of the
 * first day; people are in rows, days in columns, and a header row with day numbers, when pointed
 * at, aligns the dates (columns without a day number, such as totals, are left out). Not
 * supported, and said on the screen: sheets that mark shifts only with colours (colours are lost
 * on paste), merged cells, several weeks stacked under each other, days in rows.
 */

/** Where the roster is on the sheet (0-based rows and columns of [com.evoapps.evoshift.domain.spreadsheet.Sheet.rows]). */
data class RosterImportLayout(
    /** The column with people's names. */
    val nameColumn: Int,
    /** The first person's row: the row of the first day's cell. */
    val firstRow: Int,
    /** The first day's column. */
    val firstDayColumn: Int,
    /** The date of the first day's cell (day 1 of the month, or a chosen date). */
    val firstDate: LocalDate,
    /** A row with day numbers ("1", "2.", "1 pon", a date) that decides each column's date; null: none. */
    val headerRow: Int? = null
) {
    init {
        require(nameColumn >= 0 && firstRow >= 0 && firstDayColumn >= 0) { "Negative position" }
        require(headerRow == null || headerRow < firstRow) { "The header row is above the people" }
    }
}

/** A cell's place on the sheet. */
data class CellKey(val row: Int, val column: Int)

/** A person's row on the sheet: [name] as written there. */
data class SheetPerson(val row: Int, val name: String)

/** What a cell holds once read (times) and looked up (codes). */
sealed interface CellValue {
    /** Shifts: [ranges] read from the cell, or a code's times ([code]) or template ([templateId]). */
    data class Shifts(val ranges: List<ClockRange>, val code: String? = null, val templateId: String? = null) : CellValue
    data object DayOff : CellValue
    /** An absence: not created by the import (listed instead). */
    data class Absence(val type: AbsenceType, val code: String) : CellValue
    data object Ignored : CellValue

    /** A problem: a code without a meaning yet (the dictionary). */
    data class UnknownCode(val code: String) : CellValue
    /** A problem: the code's template no longer exists. */
    data class MissingTemplate(val code: String) : CellValue
    /** A problem: the cell's shifts overlap each other ("8-16 / 12-20"). */
    data class Overlapping(val ranges: List<ClockRange>) : CellValue

    val isProblem: Boolean get() = this is UnknownCode || this is MissingTemplate || this is Overlapping
}

/** A day cell of a person's row (a blank cell is not one). */
data class ImportCell(
    val key: CellKey,
    val date: LocalDate,
    /** The text as on the sheet, or as fixed by the person. */
    val text: String,
    val value: CellValue,
    /** Left out by the person ("Skip"). */
    val skipped: Boolean = false,
    /** Changed by the person. */
    val edited: Boolean = false
) {
    /** Still needs the person: fix or skip. */
    val needsFix: Boolean get() = value.isProblem && !skipped
}

/** A code met on the sheet: how often, and the first cell with it. */
data class CodeUse(val code: String, val count: Int, val first: CellKey)

/** The month read from the sheet with the layout, the dictionary and the person's fixes. */
data class RosterImportPlan(
    val people: List<SheetPerson>,
    /** The day columns inside the roster's month, in the sheet's order. */
    val days: Map<Int, LocalDate>,
    /** Every filled day cell of the people's rows, by row then column. */
    val cells: List<ImportCell>,
    /** Every code (not a time) on the sheet, in order of first use. */
    val codes: List<CodeUse>,
    /** Filled cells of day columns outside the roster's month (not imported). */
    val cellsOutsideMonth: Int = 0
) {
    val problemCells: List<ImportCell> get() = cells.filter { it.needsFix }
    fun cellsOf(row: Int): List<ImportCell> = cells.filter { it.key.row == row }
    fun cell(key: CellKey): ImportCell? = cells.firstOrNull { it.key == key }
}

/** Who a sheet row is in the app. */
sealed interface PersonChoice {
    /** A team member. */
    data class Member(val uid: String) : PersonChoice
    /** Added to the team as a new person (a shadow profile, as the staff import adds one). */
    data object NewPerson : PersonChoice
    /** Not imported. */
    data object Skip : PersonChoice
    /** Not decided yet (no match, or several). */
    data object Undecided : PersonChoice
}

/** One shift the import would create, from [cell] ([index]: its place in a cell with several). */
data class ImportedShiftDraft(val cell: CellKey, val index: Int, val sheetRow: Int, val shift: Shift) {
    /** A stable key for the copy tool's preview. */
    val key: String get() = "import-r${cell.row}-c${cell.column}-$index"
}

/** SH-217: reads the sheet with the layout (pure; tested on the "Kreator grafiku" grid and others). */
object RosterImportPlanner {

    /** The tag on imported shifts (Undo removes only these, and only while unpublished). */
    const val TAG = "IMPORTED"

    /** Rows a name is never taken from: a totals or signature line under the people. */
    private val NOT_PEOPLE = setOf("SUMA", "RAZEM", "ŁĄCZNIE", "TOTAL", "SUM", "РАЗОМ", "ВСЬОГО", "PODPIS", "PODPISY")

    /**
     * The plan of [rows] read with [layout] for the roster [year]-[month]: [dictionary] gives the
     * codes' meanings, [templates] the templates' times, [edits] the cells the person fixed,
     * [skipped] the cells they left out.
     */
    fun plan(
        rows: List<List<String>>,
        layout: RosterImportLayout,
        year: Int,
        month: Int,
        dictionary: RosterCodeDictionary,
        templates: List<ShiftTemplate>,
        edits: Map<CellKey, String> = emptyMap(),
        skipped: Set<CellKey> = emptySet()
    ): RosterImportPlan {
        val width = rows.maxOfOrNull { it.size } ?: 0
        val allDays = dayColumns(rows, layout, width)
        val days = allDays.filterValues { it.year == year && it.month.number == month }
        val people = people(rows, layout)
        val templatesById = templates.associateBy { it.id }
        val cells = mutableListOf<ImportCell>()
        val codes = linkedMapOf<String, CodeUse>()
        var outside = 0
        for (person in people) {
            val row = rows.getOrNull(person.row).orEmpty()
            for ((column, date) in allDays) {
                val key = CellKey(person.row, column)
                val edited = edits[key]
                val text = (edited ?: row.getOrNull(column).orEmpty()).trim()
                if (text.isEmpty()) continue
                if (column !in days) { outside++; continue }
                val ranges = ShiftTimeParser.parseRanges(text)
                val value = if (ranges != null) shiftsOrOverlap(ranges, null, null) else {
                    val code = RosterCodes.normalize(text)
                    val use = codes[code]
                    codes[code] = use?.copy(count = use.count + 1) ?: CodeUse(code, 1, key)
                    valueOf(code, dictionary[code], templatesById)
                }
                cells += ImportCell(key, date, text, value, skipped = key in skipped, edited = edited != null)
            }
        }
        return RosterImportPlan(people, days, cells, codes.values.toList(), outside)
    }

    private fun valueOf(code: String, action: RosterCodeAction?, templates: Map<String, ShiftTemplate>): CellValue = when (action) {
        null -> CellValue.UnknownCode(code)
        is RosterCodeAction.Shift -> CellValue.Shifts(listOf(action.times), code = code)
        is RosterCodeAction.Template -> templates[action.templateId]?.let { template ->
            val times = ClockRange(ShiftTimeParser.parseClock(template.startTime) ?: template.startTime, endOf(template.endTime))
            CellValue.Shifts(listOf(times), code = code, templateId = template.id)
        } ?: CellValue.MissingTemplate(code)
        RosterCodeAction.DayOff -> CellValue.DayOff
        is RosterCodeAction.Absence -> CellValue.Absence(action.type, code)
        RosterCodeAction.Ignore -> CellValue.Ignored
    }

    private fun endOf(time: String): String = ShiftTimeParser.parseClock(time, allowMidnightEnd = true)?.let { if (it == "24:00") "00:00" else it } ?: time

    private fun shiftsOrOverlap(ranges: List<ClockRange>, code: String?, templateId: String?): CellValue {
        if (ranges.size > 1) {
            val day = LocalDate(2000, 1, 1)
            val times = ranges.map { plannedTimeRange(day, it.start, it.end) }
            for (i in times.indices) for (j in i + 1 until times.size) {
                if (times[i].overlaps(times[j])) return CellValue.Overlapping(ranges)
            }
        }
        return CellValue.Shifts(ranges, code, templateId)
    }

    /**
     * Each day column's date. With a header row: the columns from the first day's on whose header
     * holds a day number; the number decides the day, the month moves on when the numbers start
     * again (…30, 31, 1, 2). Without one: the columns from the first day's on, a day each.
     */
    fun dayColumns(rows: List<List<String>>, layout: RosterImportLayout, width: Int = rows.maxOfOrNull { it.size } ?: 0): Map<Int, LocalDate> {
        val result = linkedMapOf<Int, LocalDate>()
        val header = layout.headerRow?.let { rows.getOrNull(it) }
        if (header == null) {
            for (column in layout.firstDayColumn until width) {
                result[column] = layout.firstDate.plus(DatePeriod(days = column - layout.firstDayColumn))
            }
            return result
        }
        var year = layout.firstDate.year
        var month = layout.firstDate.month.number
        var previous: Int? = null
        for (column in layout.firstDayColumn until width) {
            val day = dayNumber(header.getOrNull(column).orEmpty()) ?: continue
            if (previous != null && day <= previous) {
                if (month == 12) { month = 1; year++ } else month++
            }
            previous = day
            val date = runCatching { LocalDate(year, month, day) }.getOrNull() ?: continue
            result[column] = date
        }
        return result
    }

    /** The day of a header cell: "1", "01", "1.", "1 pon", "pon 1", "2026-02-01" (a date cell); else null. */
    fun dayNumber(cell: String): Int? {
        val text = cell.trim()
        if (text.isEmpty()) return null
        if (text.length >= 10 && text[4] == '-' && text[7] == '-') return text.substring(8, 10).toIntOrNull()?.takeIf { it in 1..31 }
        val digits = text.split(' ', '.', '\n', '/', ',').map { it.trim() }.filter { it.isNotEmpty() && it.all(Char::isDigit) }
        if (digits.size != 1) return null
        // A number only (with a dot or a weekday): "1.", "1 pon"; never "8:00" or "160,5".
        if (text.any { it == ':' }) return null
        return digits.single().toIntOrNull()?.takeIf { it in 1..31 }
    }

    /** People: the rows from the first one on with a name (blank rows and totals lines are not people). */
    fun people(rows: List<List<String>>, layout: RosterImportLayout): List<SheetPerson> =
        (layout.firstRow until rows.size).mapNotNull { row ->
            val name = rows[row].getOrNull(layout.nameColumn).orEmpty().trim().split(' ', '\t', '\n').filter { it.isNotEmpty() }.joinToString(" ")
            name.takeIf { it.isNotEmpty() && RosterCodes.normalize(it).trimEnd(':') !in NOT_PEOPLE }?.let { SheetPerson(row, it) }
        }

    /**
     * The shifts the import would create: every shift cell that is not skipped, of the rows whose
     * person is decided ([people]: a row's user id, a placeholder for someone to be added). Drafts
     * like the roster builder's own (unpublished, version 1), tagged [TAG]; templates give their
     * position and skills.
     */
    fun drafts(
        plan: RosterImportPlan,
        rosterId: String,
        people: Map<Int, Pair<String, String>>,
        templates: List<ShiftTemplate>,
        createdBy: String,
        now: String
    ): List<ImportedShiftDraft> {
        val byId = templates.associateBy { it.id }
        return plan.cells.filter { !it.skipped }.flatMap { cell ->
            val (uid, name) = people[cell.key.row] ?: return@flatMap emptyList()
            val value = cell.value as? CellValue.Shifts ?: return@flatMap emptyList()
            val template = value.templateId?.let { byId[it] }
            value.ranges.mapIndexed { index, range ->
                val shift = ShiftFactory.newShift(
                    rosterId = rosterId,
                    date = cell.date,
                    startTime = range.start,
                    endTime = range.end,
                    role = template?.role,
                    skills = template?.skills.orEmpty(),
                    createdBy = createdBy,
                    userId = uid,
                    userDisplayName = name,
                    now = now
                ).copy(tags = listOf(TAG))
                ImportedShiftDraft(cell.key, index, cell.key.row, shift)
            }
        }
    }

    /** The absence days the import leaves free (it does not create absences), by row: type and dates. */
    fun absences(plan: RosterImportPlan, rows: Set<Int>): Map<Int, List<Pair<AbsenceType, LocalDate>>> =
        plan.cells.filter { !it.skipped && it.key.row in rows }
            .mapNotNull { cell -> (cell.value as? CellValue.Absence)?.let { cell.key.row to (it.type to cell.date) } }
            .groupBy({ it.first }, { it.second })
}
